import React, { useState, useEffect, useRef, Fragment } from "react";
import {
  Grid,
  Typography,
  FormControlLabel,
  Button,
  Link,
  Checkbox,
  Container,
  CircularProgress,
  Alert,
  AlertTitle
} from "@mui/material";
import Password from "../../common/fields/Password";
import Email from "../../common/fields/Email";
import { useLocation, useNavigate, Navigate } from "react-router-dom"
import { getSafeRedirect } from "../../../utils/authRedirect"
import { getLogin } from "../../../services/LoginService";

const Login = () => {
  // States for input values ​​and errors
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({
    user: false,
    password: false,
    userText: "",
    passwordText: ""
  });
  const [credentialsError, setCredentialsError] = useState(false);
  const [responseLogin, setResponseLogin] = useState(false);
  const [secondAuth, setSecondAuth] = useState(false);
  const [methodAuth, setMethodAuth] = useState(false);
  const [interimToken, setInterimToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showQrAuth, setShowQrAuth] = useState(false)
  const location = useLocation();
  const navigate = useNavigate();
  const [showSessionExpired, setShowSessionExpired] = useState(location.state?.sessionExpired || false);

  useEffect(() => {
    if (location.state?.sessionExpired) {
      setShowSessionExpired(true);
      const timer = setTimeout(() => {
        setShowSessionExpired(false);
        navigate(location.pathname, { replace: true, state: {} });
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, []);

  const sendLogin = async () => {
    let form = {};
    let countValid = 0;

    // Force validation of the Email and Password components before continuing
    let emailError = email.trim() === "" || !/^([A-Za-z0-9+_.-]+)@([\da-zA-Z.-]+)\.+([a-zA-Z.]{2,6})$/.test(email);
    let passwordError = password.trim() === "";

    setErrors({
      user: emailError,
      password: passwordError,
      userText: emailError ? "Enter a valid email / user" : "",
      passwordText: passwordError ? "Enter a valid password" : ""
    });

    // Validate email
    if (!emailError) {
      countValid++;
      form["email"] = email.toLowerCase();
    }

    // Validate password
    if (!passwordError) {
      countValid++;
      form["password"] = password;
    }

    let newErrors = {
      status: {
        user: email.trim() !== "" && errors.userText === "" ? false : true,
        password: password.trim() !== "" && errors.passwordText === "" ? false : true
      },
      text: {
        user: email.trim() !== "" && errors.userText === "" ? "" : "Enter a valid email / user",
        password: password.trim() !== "" && errors.passwordText === "" ? "" : "Enter a valid password"
      }
    };
    setErrors(newErrors);

    let totalcount = 2;

    if (totalcount === countValid) {
      setLoading(true);
      try {
        const res = await getLogin(form);
        setLoading(false);
        if (res.userAuthenticated) {
          window.localStorage.setItem("user", res.location);
          window.localStorage.setItem("headersSetCookie", res.headersSetCookie);
          window.localStorage.setItem("additionalUserData", JSON.stringify(res.additionalUserData));
          sessionStorage.setItem("siderbarO", true);
          setResponseLogin(res);
        } else if (!res.userAuthenticated && res.secondAuthRequired) {
          setResponseLogin(false);
          setSecondAuth(true)
          setMethodAuth(res.method)
          setInterimToken(res.interimToken)
          setShowQrAuth(res.showQrAuth)
        } else {
          setResponseLogin(false);
          setCredentialsError(true);
        }
      } catch (err) {
        setLoading(false);
        setResponseLogin(false);
        setCredentialsError(true);
      }
    } else {
      window.localStorage.setItem("user", "");
      window.localStorage.setItem("headersSetCookie", "");
      window.localStorage.setItem("additionalUserData", "");
      sessionStorage.setItem("siderbarO", false);
    }
  };


  const safeRedirect = getSafeRedirect({
    pathname: location.state?.from?.pathname,
    search: location.state?.from?.search,
  })

  return (

    <Fragment>
      {responseLogin !== false && Object.keys(responseLogin).length > 0 ?
        <Navigate to={safeRedirect} replace />
        : !responseLogin && secondAuth ?
          <Navigate
            to={'/2fa'}
            exact
            state={{
              method: methodAuth,
              token: interimToken,
              showQrAuth: showQrAuth,
              from: location.state?.from,
            }}
          /> :
          <Container maxWidth="md" >
            <div className="loginPageContainer">
              {credentialsError && (
                <Alert severity="error" className="mb-4">
                  <AlertTitle>Invalid credentials</AlertTitle>
                  The username or password you entered are incorrect. Please double-check and try again.
                </Alert>
              )}

              <form 
                className="form"
                noValidate
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!loading) sendLogin();
                  }
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12}>
                    <div className="loginTitles">
                      <div className="title">
                        Welcome back
                      </div>
                      <div className="subtitle">
                        Please sign into your account
                      </div>
                    </div>
                  </Grid>
                </Grid>

                {showSessionExpired && (
                  <Alert severity="warning" style={{ marginBottom: '10px' }}>
                    <AlertTitle>Session expired</AlertTitle>
                    Your session has expired. Please sign in again.
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Email
                        id="email"
                        label="Email"
                        name="email"
                        placeholder="Email here..."
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={errors.user}
                        errorText={errors.userText}
                        classNameCustom={"textField"}
                        autoComplete="email"
                        required={true}
                        style={{ marginBottom: 10 }}
                      />
                    </div>
                    <div>
                      <Password
                        id="password"
                        label="Password"
                        name="password"
                        placeholder="Password here..."
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={errors.password}
                        errorText={errors.passwordText}
                        classNameCustom={"textField"}
                        autoComplete="current-password"
                        required={true}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12}>

                  </Grid>
                </Grid>
                <br />
                <hr />
                <div className="buttons-container">
                  <Button
                    variant="text"
                    component={Link}
                    href={"/recovery"}
                    className="forgot-password"
                    size="medium"
                  >
                    Forgot password?
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    size="large"
                    onClick={sendLogin}
                    startIcon={loading && <CircularProgress size={22} className="loginProgress" />}
                    style={{ fontSize: "0.8rem", }}
                  >
                    Sign In
                  </Button>
                </div>

              </form>
            </div>
          </Container>
      }
    </Fragment >
  );
};

export default Login;
